import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, IconButton, Stack, TextField, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { landingApi, DemoRequestError } from '../../api/landingApi';
import { C, CONTACT_EMAIL, FONT, MONO, TINTS, btnPrimarySx, btnSmallSx } from './landingTokens';

/**
 * Three step demo booking: day, then time, then details.
 *
 * Ported from the StaffOpt modal in DecisionOpt/workforce-planning so every
 * DecisionOpt product books demos the same way, into the same Web3Forms inbox.
 * A few pieces are load bearing in ways that look redundant until they break:
 *
 *   - isoOf builds the date from LOCAL parts. toISOString() converts to UTC and
 *     shifts the day backwards for anyone east of Greenwich, so an IST visitor
 *     would silently book the wrong day.
 *   - The offscreen `website` honeypot is the only spam defence on the form.
 *     When it is filled we show success and send nothing, so a bot never learns
 *     it was caught.
 *   - Both failure paths set a visible message. Never fail silently.
 *   - State resets on close, so a second open starts clean.
 */

const TIME_SLOTS = ['11:00 AM', '3:00 PM', '5:00 PM', '7:00 PM'] as const;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Step = 'date' | 'time' | 'details' | 'success';

type FormState = {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  /** Honeypot: kept offscreen, only bots fill it. */
  website: string;
};

const EMPTY: FormState = { name: '', email: '', company: '', phone: '', message: '', website: '' };

type DateCell = { iso: string; day: number; month: string; weekday: string; label: string };

/** Local-parts ISO date. See the note above, do NOT swap in toISOString(). */
const isoOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Weekdays across the next four weeks, starting today. */
const availableDates = (): DateCell[] => {
  const out: DateCell[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 28; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    if (d.getDay() === 0 || d.getDay() === 6) continue;
    const weekday = d.toLocaleString('en-US', { weekday: 'short' });
    const month = d.toLocaleString('en-US', { month: 'short' });
    out.push({ iso: isoOf(d), day: d.getDate(), month, weekday, label: `${weekday}, ${d.getDate()} ${month}` });
  }
  return out;
};

const STEP_NUMBER: Record<Step, number> = { date: 1, time: 2, details: 3, success: 3 };
const STEP_TITLE: Record<Step, string> = {
  date: 'Pick a day',
  time: 'Pick a time',
  details: 'Your details',
  success: 'Done',
};

const tileSx = (selected: boolean) => ({
  border: `1px solid ${selected ? C.blue : C.line}`,
  borderRadius: '12px',
  bgcolor: selected ? C.blueSoft : C.bg,
  color: selected ? C.blue : C.ink,
  cursor: 'pointer',
  textAlign: 'center' as const,
  fontFamily: FONT,
  transition: 'border-color 150ms ease, background-color 150ms ease',
  '&:hover': { borderColor: C.blue },
  '&:focus-visible': { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
});

const linkBtnSx = {
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 600,
  color: C.body,
  px: 1,
  '&:hover': { bgcolor: 'transparent', color: C.blue },
};

export type DemoRequestModalProps = {
  open: boolean;
  onClose: () => void;
};

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<Step>('date');
  const [date, setDate] = useState<DateCell | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on close so a second open starts clean.
  useEffect(() => {
    if (!open) {
      setStep('date');
      setDate(null);
      setSlot(null);
      setForm(EMPTY);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const dates = availableDates();
  const detailsValid =
    form.name.trim() !== '' && EMAIL_RE.test(form.email.trim()) && form.company.trim() !== '';

  const setField = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const activate = (action: () => void) => ({
    role: 'button',
    tabIndex: 0,
    onClick: action,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    },
  });

  const submit = async () => {
    if (!date || !slot || !detailsValid) return;
    // A bot filled the honeypot: show success, send nothing.
    if (form.website.trim()) {
      setStep('success');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await landingApi.submitDemoRequest({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        preferredDate: date.iso,
        preferredTime: slot,
      });
      setStep('success');
    } catch (err) {
      if (err instanceof DemoRequestError && err.network) {
        setError(
          `We could not reach our servers just now. Check your connection, or email ${CONTACT_EMAIL} and we will pick it up.`
        );
      } else {
        setError(`We could not send that request. Please email ${CONTACT_EMAIL} and we will pick it up.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    '& .MuiInputBase-root': { fontFamily: FONT, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily: FONT },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="demo-request-title"
      PaperProps={{
        sx: {
          maxWidth: 540,
          borderRadius: '18px',
          bgcolor: C.bg,
          color: C.ink,
          backgroundImage: 'none',
          boxShadow: '0 30px 60px -20px rgba(18,23,42,.35)',
          fontFamily: FONT,
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(11,17,28,0.5)', backdropFilter: 'blur(4px)' } } }}
    >
      <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2.5, sm: 3 }, pb: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography id="demo-request-title" sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 21, letterSpacing: '-0.02em' }}>
              {step === 'success' ? 'Request received' : 'Schedule a demo'}
            </Typography>
            {step !== 'success' && (
              <Typography sx={{ fontFamily: MONO, fontSize: 12, color: C.muted, mt: 0.5, letterSpacing: '0.02em' }}>
                {STEP_NUMBER[step]} of 3 · {STEP_TITLE[step]}
              </Typography>
            )}
          </Box>
          <IconButton size="small" aria-label="Close" onClick={onClose} sx={{ color: C.muted, mt: -0.5, mr: -1 }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {step !== 'success' && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 2 }} aria-hidden="true">
            {[1, 2, 3].map(n => (
              <Box
                key={n}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 999,
                  bgcolor: n <= STEP_NUMBER[step] ? C.blue : C.line,
                  transition: 'background-color 200ms ease',
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pb: { xs: 2.5, sm: 3.5 } }}>
        {step === 'date' && (
          <>
            <Typography sx={{ fontFamily: FONT, color: C.body, fontSize: 14.5, mb: 1.5 }}>
              A 20-minute walkthrough on your own routes. Weekdays over the next four weeks.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))' },
                gap: 1,
                maxHeight: 300,
                overflowY: 'auto',
                p: 0.25,
              }}
            >
              {dates.map(cell => (
                <Box
                  key={cell.iso}
                  {...activate(() => {
                    setDate(cell);
                    setStep('time');
                  })}
                  aria-label={cell.label}
                  sx={{ ...tileSx(date?.iso === cell.iso), py: 1.1 }}
                >
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {cell.weekday}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19, lineHeight: 1.3 }}>{cell.day}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>{cell.month}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        {step === 'time' && date && (
          <>
            <Typography sx={{ fontFamily: FONT, color: C.body, fontSize: 14.5, mb: 1.5 }}>
              {date.label}. All times are IST.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
              {TIME_SLOTS.map(option => (
                <Box
                  key={option}
                  {...activate(() => {
                    setSlot(option);
                    setStep('details');
                  })}
                  sx={{ ...tileSx(slot === option), py: 1.6, fontWeight: 700, fontSize: 15.5 }}
                >
                  {option}
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, color: C.muted, ml: 0.75 }}>
                    IST
                  </Box>
                </Box>
              ))}
            </Box>
            <Button onClick={() => setStep('date')} sx={{ ...linkBtnSx, mt: 2 }}>
              ← Change day
            </Button>
          </>
        )}

        {step === 'details' && date && slot && (
          <Stack spacing={1.75}>
            <Box
              sx={{
                alignSelf: 'flex-start',
                fontFamily: MONO,
                fontSize: 12,
                color: C.blue,
                bgcolor: C.blueSoft,
                borderRadius: 999,
                px: 1.4,
                py: 0.5,
              }}
            >
              {date.label} · {slot} IST
            </Box>
            <TextField label="Name" required value={form.name} onChange={setField('name')} autoComplete="name" fullWidth sx={fieldSx} />
            <TextField
              label="Work email"
              type="email"
              required
              value={form.email}
              onChange={setField('email')}
              autoComplete="email"
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Company"
              required
              value={form.company}
              onChange={setField('company')}
              autoComplete="organization"
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Phone (optional)"
              value={form.phone}
              onChange={setField('phone')}
              autoComplete="tel"
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="What are you routing today? (optional)"
              value={form.message}
              onChange={setField('message')}
              multiline
              minRows={3}
              fullWidth
              sx={fieldSx}
            />

            {/* Honeypot: offscreen, never shown to a person. */}
            <Box
              component="input"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={setField('website')}
              sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
              <Button onClick={() => setStep('time')} sx={linkBtnSx}>
                ← Back
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={!detailsValid || submitting}
                onClick={submit}
                sx={{ ...btnPrimarySx, ...btnSmallSx, '&.Mui-disabled': { bgcolor: C.line, color: C.muted } }}
              >
                {submitting ? 'Sending…' : 'Schedule demo'}
              </Button>
            </Stack>
          </Stack>
        )}

        {step === 'success' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 1.5, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: TINTS.green.bg,
                color: TINTS.green.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 16, lineHeight: 1.65, color: C.body }}>
              Thanks. We will confirm{' '}
              <Box component="strong" sx={{ color: C.ink }}>
                {date?.label}
              </Box>{' '}
              at{' '}
              <Box component="strong" sx={{ color: C.ink }}>
                {slot} IST
              </Box>{' '}
              by email.
            </Typography>
            <Typography sx={{ fontFamily: FONT, color: C.muted, fontSize: 13.5 }}>
              Prefer email? Reach us at {CONTACT_EMAIL}
            </Typography>
            <Button variant="contained" disableElevation onClick={onClose} sx={{ ...btnPrimarySx, ...btnSmallSx, px: 4 }}>
              Done
            </Button>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};

export default DemoRequestModal;
